import { mediaQueryUp } from 'components/utils/breakpoint'
import React, { useContext } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { rem } from 'polished'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView } from 'selectors/offerPageSelectors'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import FlashHotelOptionPriceDetails from './FlashHotelOptionPriceDetails'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import GeoContext from 'contexts/geoContext'

import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import { useInclusionTravelPeriodFilter } from 'hooks/useInclusionTravelPeriodFilter'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { getOnlyNRoomLeftLabel } from 'lib/getUrgencyLabels'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import { InclusionsV2Section } from 'components/OfferPage/FlashHotel/InclusionsV2Section'
import { InclusionsSection } from 'components/OfferPage/FlashHotel/InclusionsSection'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import config from 'constants/config'

const ContentGrid = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  grid-template-columns: 1fr;
  padding: ${rem(16)};

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr ${rem(392)};
    grid-template-rows: min-content 1fr;
    grid-column-gap: ${rem(24)};
    grid-row-gap: ${rem(8)};
  }

  ${mediaQueryUp.md} {
    padding: ${rem(18)} ${rem(32)};
    grid-template-columns: ${rem(256)} 1fr ${rem(372)};
  }

  &.single-pricing {
    ${mediaQueryUp.sm} {
      grid-template-columns: 1fr ${rem(230)};
    }

    ${mediaQueryUp.md} {
      grid-template-columns: ${rem(350)} 1fr ${rem(230)} ;
      grid-column-gap: ${rem(40)};
      grid-row-gap: 0;
    }
  }
`

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.sm} {
    grid-column: 1;
  }

  ${mediaQueryUp.md} {
    text-align: left;
  }
`

const StyledInclusionsSection = styled(BodyText)`
  display: flex;
  flex-direction: column;
  grid-column: 1;
  grid-row: 2;

  ${mediaQueryUp.sm} {
    align-items: flex-start;
  }

  ${mediaQueryUp.md} {
    grid-column: 2;
    grid-row: 1;
  }
`

const BookingSection = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1;
  grid-row: 3;
  justify-content: space-between;

  ${mediaQueryUp.sm} {
    grid-column: 2;
    grid-row: 1 / span 2;
    align-items: stretch;
  }

  ${mediaQueryUp.md} {
    grid-column: 3;
    grid-row: 1;
  }

  > * + * {
    margin-top: ${rem(16)};
  }

  &.single-pricing {
    ${mediaQueryUp.xs} {
      grid-column: 2;
      grid-row: 1 / span 2;
      align-items: stretch;
    }

    ${mediaQueryUp.md} {
      grid-column: 3;
    }
  }
`

const StyledLabelGroup = styled(LabelGroup)`
  margin-top: ${rem(8)};
`

const StyledOfferPackagePartnershipLabel = styled(OfferPackagePartnershipLabel)`
  margin-top: ${rem(12)};
`

interface Props {
  optionNumber: number;
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  checkIn: moment.Moment;
  duration: number;
  flightDestination?: App.Airport;
  previewMode: boolean;
}

function FlashHotelRateOption(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    duration,
    optionNumber,
    flightDestination,
    previewMode,
  } = props

  const { partnership } = useContext(GeoContext)
  const availableForPurchase = useIsAvailableForPurchase()
  const {
    package: pkg,
  } = packageView

  const showPartnership =
    (packageView.hotelAvailable &&
      !!partnership?.hasEarn &&
      offer.canEarnPartnershipPoints &&
      pkg.partnership &&
      partnership?.code !== VIRGIN_VELOCITY_PARTNERSHIP_CODE)

  const showPrices = (packageView.capacityValid && packageView.hotelAvailable) || previewMode

  const inclusions = useInclusionTravelPeriodFilter(pkg.inclusions, checkIn, checkIn?.clone().add(duration, 'days'))
  const bonusInclusions = useInclusionTravelPeriodFilter(pkg.bonusInclusions, checkIn, checkIn?.clone().add(duration, 'days'))

  const hasInclusionsV1 = pkg.inclusions && pkg.inclusions.length > 0

  const urgencyLabel = getOnlyNRoomLeftLabel((packageView.availableRooms ?? 0), true, config.RATE_OPTION_ONLY_N_ROOMS_LEFT_THRESHOLD)

  return (
    <ContentGrid data-testid={pkg.uniqueKey}>
      <TitleSection>
        <div>
          <BodyText variant="medium" colour="neutral-two">Option {optionNumber}</BodyText>
          <Heading variant="heading6">{packageView.package.name}</Heading>
          {availableForPurchase && <FlashCancellationPolicies
            checkInDate={checkIn}
            propertyTimezoneOffset={offer.property.timezoneOffset}
            cancellationPolicy={pkg.roomRate.cancellationPolicy}
          />}
          {!!showPartnership && <StyledOfferPackagePartnershipLabel
            offer={offer}
            price={packageView.hotelPrice}
          />}
          {urgencyLabel && <StyledLabelGroup>
            <UrgencyLabel variant="critical1">{urgencyLabel}</UrgencyLabel>
          </StyledLabelGroup>}
        </div>
      </TitleSection>
      <StyledInclusionsSection variant="medium">
        <LargeContentToggle
          maxHeight={208}
          showMoreText="View all inclusions"
          showLessText="View less inclusions"
        >
          {hasInclusionsV1 && (bonusInclusions || inclusions) && <InclusionsSection inclusions={inclusions} bonusInclusions={bonusInclusions} />}
          {!hasInclusionsV1 && packageView.package.inclusionsV2 && <InclusionsV2Section inclusions={packageView.package.inclusionsV2} />}
        </LargeContentToggle>
      </StyledInclusionsSection>
      <BookingSection>
        {showPrices && <FlashHotelOptionPriceDetails
          packageView={packageView}
          flightDestination={flightDestination}
        />}
      </BookingSection>
    </ContentGrid>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  previewMode: appState.config.previewMode,
}))(FlashHotelRateOption)
